import type { Term } from "@/lib/campaign";

/**
 * How a new-lead trigger routes the leads it makes (ADR-175 in platform): the tags every request is
 * read as carrying, the split of new leads across the tenant's firms, and the answers that make a lead
 * not qualified. Stored in the trigger's `options` as `extra_tags`, `firm_split` and `qualify`.
 *
 * The rules are core-api's `src/lead_routing.py`; the copies here keep what it would refuse off the
 * form and say why first. Firms are named by id, as a trigger's campaign is. Whether each firm is the
 * tenant's is asked when it is saved. Pure, for
 * `test/trigger-routing.test.ts`.
 */

export const EXTRA_TAGS = "extra_tags";
export const FIRM_SPLIT = "firm_split";
export const QUALIFY = "qualify";
export const ROUTING_OPTIONS = [EXTRA_TAGS, FIRM_SPLIT, QUALIFY] as const;

export const MAX_EXTRA_TAGS = 10;
export const MAX_SPLIT = 20;
export const MAX_RULES = 30;
export const MAX_RULE_VALUES = 50;
const REASON_RE = /^[a-z][a-z0-9_]{0,49}$/;

export const RULE_OPS = ["in", "not_in", "equals", "empty", "not_empty"] as const;
export type RuleOp = (typeof RULE_OPS)[number];

/** The ops that compare the answer with values; the other two ask only whether there is one. */
export function comparesValues(op: string): boolean {
  return op === "in" || op === "not_in" || op === "equals";
}

const OP_WORDS: Record<RuleOp, Term> = {
  in: { label: "is one of", description: "Not qualified when the answer is one of these." },
  not_in: {
    label: "is none of",
    description: "Not qualified when there is an answer and it is none of these. No answer passes; use Is blank for that.",
  },
  equals: { label: "is", description: "Not qualified when the answer is this one." },
  empty: { label: "is blank", description: "Not qualified when the request leaves the answer out or blank." },
  not_empty: { label: "is filled in", description: "Not qualified when the request carries any answer." },
};

export function opTerm(op: string): Term {
  return OP_WORDS[op as RuleOp] ?? { label: op, description: "" };
}

export interface SplitRow {
  /** A firm's id as the picker holds it, as text; saved as the number, as a campaign is named. */
  firm_id: string;
  /** The share as typed: a whole number from 0 to 100. */
  pct: string;
}

export interface RuleRow {
  /** The request's key for the answer, as the sender spells it. */
  field: string;
  op: string;
  values: string[];
  reason: string;
}

export interface RoutingValues {
  extra_tags: string[];
  split: SplitRow[];
  rules: RuleRow[];
}

export const NO_ROUTING: RoutingValues = { extra_tags: [], split: [], rules: [] };

const isText = (value: unknown): value is string => typeof value === "string";

/** The stored options, as the form edits them. An entry shaped some other way is left out. */
export function routingValues(options: Record<string, unknown>): RoutingValues {
  const tags = options[EXTRA_TAGS];
  const split = options[FIRM_SPLIT];
  const rules = options[QUALIFY];
  return {
    extra_tags: Array.isArray(tags) ? tags.filter(isText) : [],
    split: Array.isArray(split)
      ? split
          .filter(
            (entry): entry is { firm_id: number; pct: number } =>
              Number.isInteger(entry?.firm_id) && typeof entry?.pct === "number",
          )
          .map((entry) => ({ firm_id: String(entry.firm_id), pct: String(entry.pct) }))
      : [],
    rules: Array.isArray(rules)
      ? rules
          .filter((rule) => isText(rule?.field) && isText(rule?.op) && isText(rule?.reason))
          .map((rule) => ({
            field: rule.field,
            op: rule.op,
            values: Array.isArray(rule.values) ? rule.values.filter(isText) : [],
            reason: rule.reason,
          }))
      : [],
  };
}

function share(pct: string): number | null {
  const text = pct.trim();
  if (!/^\d{1,3}$/.test(text)) return null;
  const value = Number(text);
  return value <= 100 ? value : null;
}

/** The row's firm id, or null while none is chosen. */
function firmId(row: SplitRow): number | null {
  return /^[1-9]\d{0,9}$/.test(row.firm_id.trim()) ? Number(row.firm_id) : null;
}

/** The shares added up, counting only those that are whole numbers from 0 to 100. */
export function splitTotal(split: readonly SplitRow[]): number {
  return split.reduce((sum, row) => sum + (share(row.pct) ?? 0), 0);
}

/** Why the routing cannot be saved as it stands, or null: `lead_routing.options_problem`. */
export function routingProblem(values: RoutingValues): string | null {
  // A blank is the comma just typed, not a tag: it is never saved.
  const tags = values.extra_tags.map((tag) => tag.trim()).filter(Boolean);
  if (tags.length > MAX_EXTRA_TAGS) return `Up to ${MAX_EXTRA_TAGS} extra tags.`;
  if (tags.some((tag) => tag.length > 64)) {
    return "An extra tag is up to 64 characters.";
  }
  if (values.split.length > 0) {
    if (values.split.length > MAX_SPLIT) return `A split names up to ${MAX_SPLIT} firms.`;
    if (values.split.some((row) => firmId(row) === null)) return "Choose a firm on every row of the split.";
    if (values.split.some((row) => share(row.pct) === null)) {
      return "Each firm's share is a whole number from 0 to 100.";
    }
    const firms = values.split.map(firmId);
    if (new Set(firms).size !== firms.length) return "A firm is in the split twice.";
    if (splitTotal(values.split) <= 0) return "Give at least one firm in the split a share.";
  }
  if (values.rules.length > MAX_RULES) return `Up to ${MAX_RULES} rules.`;
  for (const [index, rule] of values.rules.entries()) {
    const which = `Rule ${index + 1}:`;
    if (!rule.field.trim()) return `${which} say which answer it reads, by the request's key for it.`;
    if (!(RULE_OPS as readonly string[]).includes(rule.op)) return `${which} choose how it compares.`;
    if (!REASON_RE.test(rule.reason)) {
      return `${which} the reason is lowercase letters, digits and underscores, like has_lawyer.`;
    }
    if (comparesValues(rule.op)) {
      const answers = rule.values.filter((value) => value.trim());
      if (answers.length === 0) return `${which} add the answers it compares with.`;
      if (answers.length > MAX_RULE_VALUES) return `${which} up to ${MAX_RULE_VALUES} answers.`;
      if (rule.op === "equals" && answers.length > 1) return `${which} "is" compares with one answer; use "is one of".`;
    }
  }
  return null;
}

/** The options a trigger is saved with, and none it would not use: an empty list is no option. */
export function routingOptions(values: RoutingValues): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  const tags = values.extra_tags.map((tag) => tag.trim()).filter(Boolean);
  if (tags.length > 0) out[EXTRA_TAGS] = tags;
  if (values.split.length > 0) {
    out[FIRM_SPLIT] = values.split.map((row) => ({ firm_id: firmId(row) ?? 0, pct: share(row.pct) ?? 0 }));
  }
  if (values.rules.length > 0) {
    out[QUALIFY] = values.rules.map((rule) => ({
      field: rule.field.trim(),
      op: rule.op,
      ...(comparesValues(rule.op) ? { values: rule.values.map((value) => value.trim()).filter(Boolean) } : {}),
      reason: rule.reason,
    }));
  }
  return out;
}

/** What a request's result says of its routing, as a sentence to follow the rest: the firm and how
 *  it was chosen, and why a lead is not qualified. Empty for a trigger that does not route. */
export function routingResultText(result: Record<string, unknown>): string {
  let out = "";
  if (typeof result.firm_source === "string") {
    const firm = typeof result.firm === "string" ? result.firm : null;
    const ignored = typeof result.firm_id_ignored === "string" ? result.firm_id_ignored : null;
    const how: Record<string, string> = {
      split: "drawn from the split",
      payload: "named in the request",
      lead: "the lead's own",
    };
    out += firm
      ? ` Firm ${firm}, ${how[result.firm_source] ?? result.firm_source}.`
      : result.firm_source === "lead"
        ? " The lead has no firm, so the trigger's campaign took them."
        : " No firm: none in the split is the tenant's now.";
    if (ignored !== null) out += ` The request's firm id ${ignored} is not one of your firms, so it was passed over.`;
  }
  if (result.qualified === false) {
    const reason = typeof result.nq_reason === "string" ? result.nq_reason : "a rule";
    out += ` Not qualified (${reason}): on the list, never dialed.`;
  }
  return out;
}
